import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { useAuth, HOME_BY_ROLE } from "@/lib/auth";
import { apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n/i18n";

export const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") ? n : null);

export default function Login() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login, user } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get("next"));

  const target = (u) => (u.role === "company" && !u.companyId ? `/empresas/inscripcion/${u.applicationId}` : HOME_BY_ROLE[u.role] || "/");
  if (user && !busy) return <Navigate to={next || target(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError(t("Introduce tu email y contraseña")); return; }
    setBusy(true);
    setError("");
    try {
      const d = await login(email.trim(), password);
      toast.success(t("Sesión iniciada"));
      nav(next || target(d.user), { replace: true });
    } catch (err) {
      setError(t(apiError(err).message));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="login-page">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 py-14 lg:px-10 lg:py-20">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Foro de Empleo UPV 2027")}</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="login-title">{t("INICIAR SESIÓN")}</h1>
          <p className="mt-3 max-w-md text-slate-600">{t("Un único acceso para participantes, empresas y equipo UPV.")}</p>
          <form onSubmit={submit} noValidate className="mt-8 max-w-md space-y-5 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input id="login-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Contraseña")}</Label>
              <Input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl" data-testid="login-password-input" />
            </div>
            {error && <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display text-base font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-80" data-testid="login-submit-button">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />} {t("ENTRAR")}
            </button>
            <Link to="/recuperar" className="mt-3 block text-center text-sm font-semibold text-upv hover:underline" data-testid="login-forgot-link">{t("¿Has olvidado tu contraseña?")}</Link>
            <div className="flex flex-col gap-2 border-t border-slate-100 pt-5 text-sm">
              <Link to="/registro" className="font-semibold text-upv hover:underline" data-testid="login-register-link">{t("¿Eres estudiante? Regístrate")}</Link>
              <Link to="/empresas/inscripcion" className="font-semibold text-slate-700 hover:underline" data-testid="login-company-link">{t("¿Eres una empresa? Inscríbela en el Foro")}</Link>
            </div>
          </form>
        </div>
      </main>
      <PageFooter />
    </div>
  );
}
